import React, { MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { LanguageContext, MessageContext } from '../../../Common/Contexts'
import AutoComplete from '../../../Common/Components/AutoComplete'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { ConfigureReport as ConfigureReportLanguage, ReportOptions as ReportOptionsLanguage } from '../../../../../i18n'

import { FOLDERS_SEPARATOR_BACKEND, FOLDERS_SEPARATOR_DISPLAY } from '../../../MainMenu/ReportList/Constants'
import { ReportListItemResponse } from '../../../../NonReact/common/Types/Types'
import { retrieveReportList } from '../../../../NonReact/common/API/Reporting'

/**
 * Component properties type.
 */
type Props = {
    classNames?: string
    folderPath: string
    setFolderPath: (value: string) => void
    autoCompleteCallback: () => void
    isOpen: boolean
    setIsOpen: (value: boolean) => void
}

/**
 * Folder component in report tab.
 *
 * @param folderPath The string value of the folder path.
 * @param setFolderPath The setter method for the folder path.
 * @param autoCompleteCallback A method to be called on the opening of the popper.
 * @param isOpen A flag defining the open state of the popper.
 * @param setIsOpen The setter method for the open flag.
 *
 */
const FolderComponent: React.FC<Props> = ({
    classNames,
    folderPath,
    setFolderPath,
    autoCompleteCallback,
    isOpen,
    setIsOpen
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    const { displayError } = useContext(MessageContext)
    const [folderList, setFolderList] = useState<string[]>([])
    // eslint-disable-next-line
    const [isDataLoading, setDataLoading] = useState(false)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    /**
     * A method that is called to build a list of folder options from the report list response.
     *
     * @param reportsFromResponse Report list from request response.
     */
    const getFolderList = (reportsFromResponse: ReportListItemResponse[]): void => {
        let folders = [] as string[]

        for (let i = 0; i < reportsFromResponse.length; i += 1) {
            // Build array of file paths.
            const description = reportsFromResponse[i].Description
            // Split string into list via a non-breaking space character.
            const filePath = description.split(FOLDERS_SEPARATOR_BACKEND)

            const foldersTemp = [] as string[]
            let newFilePath = filePath

            for (let k = 0; k < filePath.length; k += 1) {
                if (newFilePath.length > 1) {
                    // Make copy of report
                    const copyArr = newFilePath.slice(0)
                    // Trim previous folder option off the end.
                    newFilePath = copyArr.splice(0, newFilePath.length - 1)

                    let stringifyArray = newFilePath.join(FOLDERS_SEPARATOR_DISPLAY)
                    // Add ending slash on all folders.
                    stringifyArray = `${stringifyArray}${FOLDERS_SEPARATOR_DISPLAY}`
                    const stringExists = folders.indexOf(stringifyArray) > -1

                    if (!stringExists) {
                        foldersTemp.push(stringifyArray)
                    }
                }
            }
            // Set smallest folder paths to be at the start of array.
            foldersTemp.reverse()
            folders = folders.concat(foldersTemp)
        }

        if (isMounted.current) {
            setFolderList(folders)
        }
    }

    /**
     * Initialises report list component data.
     */
    const initialiseReportListData = async (): Promise<void> => {
        try {
            setDataLoading(true)
            // Retrieve list of reports from API.
            const reportListResponse = await retrieveReportList()

            if (reportListResponse) {
                getFolderList(reportListResponse.ReportList)
            }
        } catch (error) {
            displayError(error as Error)
        } finally {
            if (isMounted.current) {
                setDataLoading(false)
            }
        }
    }

    useEffect(() => {
        // Actually initialise component data.
        initialiseReportListData()
    }, [])

    return (
        <AutoComplete
            classNames={classNames}
            textInputLabel={language?.get(ReportOptionsLanguage.ReporttablabelFolder)}
            textInputValue={folderPath}
            setTextInputValue={setFolderPath}
            selectList={folderList}
            noListItemsMessage={language?.get(ConfigureReportLanguage.ReporttypelabelNolistitems)}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            autoCompleteCallback={autoCompleteCallback}
        />
    )
}

export default React.memo(FolderComponent)
